/**
 * Reine Helfer der Wochenplan-Ansicht (ohne Rendering-Abhängigkeiten).
 */

import type { AvailabilityStatus, TripView } from "./types";
import { isoWeekToMonday } from "../../lib/isoWeek";

// Katalog-Schlüssel je Wochentag (sprach-neutral; Anzeige über t()).
export const DAY_LABEL_KEYS = [
  "weeklyPlan.day.mon",
  "weeklyPlan.day.tue",
  "weeklyPlan.day.wed",
  "weeklyPlan.day.thu",
  "weeklyPlan.day.fri",
] as const;

// Datums-Format der Tages-Überschrift (entspricht dem bisherigen UTC-Format).
export const DAY_DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  timeZone: "UTC",
};

// Kurzes Tag./Monat.-Format für den Wochen-Datumsbereich (z. B. „19.10.").
const RANGE_DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  timeZone: "UTC",
};

/** ISO-Datum ("YYYY-MM-DD", UTC) eines UTC-`Date`. */
function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Die Wochennummer aus einem "YYYY-WNN"-String (z. B. 43 aus "2025-W43").
 * Fällt bei ungültiger Eingabe auf 0 zurück (defensiv, kein Throw im Render).
 */
export function isoWeekNumber(isoWeek: string): number {
  const m = /^\d{4}-W(\d{2})$/.exec(isoWeek);
  return m ? Number(m[1]) : 0;
}

/**
 * Formatiert den Mo–Fr-Datumsbereich einer ISO-Woche, z. B. „19.10. bis 23.10."
 * (DE) bzw. „19/10 to 23/10" (EN). `formatDate`/`sep` kommen aus i18n.
 */
export function isoWeekDateRange(
  isoWeek: string,
  formatDate: (iso: string, opts?: Intl.DateTimeFormatOptions) => string,
  sep: string,
): string {
  let monday: Date;
  try {
    monday = isoWeekToMonday(isoWeek);
  } catch {
    return "";
  }
  const friday = new Date(monday.getTime() + 4 * 24 * 60 * 60 * 1000);
  const start = formatDate(toIsoDate(monday), RANGE_DATE_OPTS);
  const end = formatDate(toIsoDate(friday), RANGE_DATE_OPTS);
  return `${start}${sep}${end}`;
}

/** Status eines beliebigen Mitglieds in einer Fahrt, oder null (keine Angabe). */
export function statusOf(
  trip: TripView,
  userId: string | undefined,
): AvailabilityStatus | null {
  if (userId == null) return null;
  if (trip.wish.some((m) => m.userId === userId)) return "wish";
  if (trip.optional.some((m) => m.userId === userId)) return "optional";
  if (trip.blocker.some((m) => m.userId === userId)) return "blocker";
  return null;
}

/** Der eigene Status in einer Fahrt (Alias zu statusOf für die eigene Sicht). */
export const ownStatus = statusOf;

/** Heutiges ISO-Datum ("YYYY-MM-DD") in lokaler Zeit. */
export function todayIsoDate(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Formatiert einen ISO-Zeitstempel als kurzes Datum+Zeit für die
 * Kommentar-Anzeige, gebunden an die Aktive_Sprache (Req 9.1/9.2).
 */
export function formatNoteTime(
  iso: string,
  formatDate: (iso: string, opts?: Intl.DateTimeFormatOptions) => string,
  formatTime: (iso: string, opts?: Intl.DateTimeFormatOptions) => string,
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const datePart = formatDate(iso, { day: "2-digit", month: "2-digit" });
  const timePart = formatTime(iso, { hour: "2-digit", minute: "2-digit" });
  return `${datePart} ${timePart}`;
}

/**
 * Entscheidet, ob der „Benachrichtigen beim Verlassen"-Prompt gezeigt werden
 * soll (Req 4.4, 5.1, 5.2). Rein: der Prompt erscheint nur, wenn die
 * Notify_On_Change-Einstellung der Gruppe aktiv ist UND der Nutzer den
 * Wochenplan in dieser Sitzung geändert hat.
 */
export function shouldPromptOnLeave(
  settingEnabled: boolean,
  hasChanged: boolean,
): boolean {
  return settingEnabled && hasChanged;
}

/**
 * Entscheidet, ob ein In-App-Navigationsziel den Wochenplan-Bereich der Gruppe
 * verlässt (Req 5.1, 5.3). Der Wochenplan lebt unter
 * `/groups/{id}/plan` bzw. `/groups/{id}/plan/{week}`. Ein reiner
 * Wochenwechsel bleibt innerhalb dieses Präfixes und gilt NICHT als Verlassen,
 * sodass der „Benachrichtigen beim Verlassen"-Prompt dabei nicht erscheint.
 * Alles außerhalb des Präfixes (Gruppen-Liste, Einstellungen, Profil, Back …)
 * gilt als Verlassen.
 */
export function isLeavingPlanRoute(
  groupId: string,
  nextPathname: string,
): boolean {
  const prefix = `/groups/${groupId}/plan`;
  return !(nextPathname === prefix || nextPathname.startsWith(`${prefix}/`));
}
